import {
  confirmMethod,
  confirmOpening,
  deleteAnomaly,
  getStoreCameraConfigOptions,
  getStoreCameraListOptions,
  openDoor,
  useAiVideoUrl,
  useAnomalies,
  useBoutiques,
  useCheckinKiosks,
  useCheckoutKiosks,
  useLiveAiArticles,
  useLiveAnomalies,
  usePendingOpeningEvents,
  useStoreOpeningState,
  useVideoCameras,
  type AiVideoCamera,
} from '@gozoki/api'
import { type Kiosk as KioskType, type UniqueArticle } from '@gozoki/api-types'
import { Button } from '@mui/material'
import Box from '@mui/material/Box'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { LockOpen } from 'lucide-react'
import { useCallback, useContext, useEffect, useState } from 'react'

import { RECEPTION_OPENING_EVENT_TYPES } from '@gozoki/api-types'
import { z } from 'zod/v3'
import '../../../assets/styles/CameraFont.module.css'
import InterventionButtons from '../../../components/buttons/Intervention.tsx'
import { useToast } from '../../../components/communication/Toast.tsx'
import Page from '../../../components/Page.tsx'
import { StoreCameraViewer } from '../../../components/routes/camera-config/store-camera-viewer'
import AnomalyCards from '../../../components/routes/cameras/AnomalyCards.tsx'
import CameraWindow from '../../../components/routes/cameras/CameraWindow.tsx'
import { getOpeningEventCard } from '../../../components/routes/cameras/openingEventCards.config.tsx'
import { AuthContext } from '../../../utils/context/auth.context'
import { StoresPicker } from '../../../utils/dashboard/storesPicker.tsx'
import { useOpeningEventUpdatedNotifications } from '../../../utils/websockets/useOpeningEventUpdatedNotifications.ts'

import { withResourcePermission } from '../../../components/RequireResourcePermission'
export const Route = createFileRoute('/_authenticated/cameras/')({
  component: withResourcePermission('cameras', 'read')(CamerasPages),
  validateSearch: z.object({
    storeId: z.number().int().positive().optional(),
  }),
})

function CamerasPages() {
  const navigate = Route.useNavigate()
  const storeId = Route.useSearch().storeId?.toString() ?? '3'
  const { hasResourcePermission } = useContext(AuthContext)

  const [centralVideoId, setCentralVideoId] = useState<string | null>(null)

  const [Toast, showToast] = useToast()

  const { data: boutiques } = useBoutiques()
  const { data: cameras } = useVideoCameras(storeId)
  const centralCameraLabel = cameras?.find((c) => c.id === centralVideoId)?.label
  const cameraConfigQuery = useQuery({
    ...getStoreCameraConfigOptions(Number.parseInt(storeId, 10)),
  })
  const usesNewCameraConfig = cameraConfigQuery.data?.config != null
  const cameraListQuery = useQuery({
    ...getStoreCameraListOptions(usesNewCameraConfig ? Number.parseInt(storeId, 10) : undefined),
  })
  const { data: checkinKiosks } = useCheckinKiosks(Number.parseInt(storeId, 10))
  const { data: checkoutKiosks } = useCheckoutKiosks(Number.parseInt(storeId, 10))
  const { data: storeOpeningState } = useStoreOpeningState(Number.parseInt(storeId, 10))

  const isAttendedMode =
    storeOpeningState?.openingMode === 'ATTENDED' ||
    storeOpeningState?.openingMode === 'ATTENDED_PRE_AUTHORIZATION'

  const checkinKioskId = checkinKiosks?.length ? checkinKiosks[0].id : null

  const { data: anomalies } = useAnomalies(storeId)
  useLiveAnomalies(storeId)
  useLiveAiArticles(storeId)

  const { data: pendingOpeningEvents, refetch: refetchPendingOpeningEvents } =
    usePendingOpeningEvents(Number(storeId))

  const { mutate: mutateConfirmMethod, isPending: isConfirmMethodPending } = useMutation({
    mutationFn: confirmMethod,
    onSuccess: () => refetchPendingOpeningEvents(),
    onError: () => showToast({ severity: 'error', message: 'Erreur lors de la confirmation' }),
  })

  const { mutate: mutateConfirmOpening, isPending: isConfirmOpeningPending } = useMutation({
    mutationFn: confirmOpening,
    onSuccess: () => refetchPendingOpeningEvents(),
    onError: () =>
      showToast({ severity: 'error', message: "Erreur lors de la confirmation d'ouverture" }),
  })

  useOpeningEventUpdatedNotifications(
    useCallback(
      (data) => {
        if (data.storeId === Number(storeId)) refetchPendingOpeningEvents()
      },
      [storeId, refetchPendingOpeningEvents],
    ),
  )

  const { data: mainVideoUrl } = useAiVideoUrl(storeId, centralVideoId)

  const { mutate: mutateDeleteAnomaly } = useMutation({
    mutationFn: ({ sId, aId }: { sId: string; aId: string }) => deleteAnomaly(sId, aId),
    onError: () => {
      showToast({
        message: "Une erreur est survenue lors de la suppression de l'anomalie",
        severity: 'error',
      })
    },
  })

  const handleDeleteAnomaly = (anomalyId: string) => {
    mutateDeleteAnomaly({ aId: anomalyId, sId: storeId })
  }

  const handleVideoClick = (videoId: string) => {
    if (videoId !== centralVideoId) {
      setCentralVideoId(videoId)
    }
  }

  useEffect(() => {
    if (cameras && cameras.length > 0) {
      setCentralVideoId(cameras[0].id)
    }
  }, [cameras])

  const handleChangeStore = (sId: number) => navigate({ to: '/cameras', search: { storeId: sId } })

  return (
    <Page>
      <div style={{ marginLeft: '25px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '20px',
            // MarginTop: '10px',
            height: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              height: '100%',
              justifyContent: 'center',
              // Flex: 3,
            }}
          >
            <div
              style={{
                color: 'darkgreen',
                fontFamily: 'BlockBerthold_regular',
                fontSize: '40px',
                fontWeight: '400',
                // Display: 'flex',
              }}
            >
              Caméras
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <InterventionButtons
                  checkType="CHECK-IN"
                  kioskId={checkinKioskId}
                  storeId={Number(storeId)}
                />
              </div>
              <div style={{ flex: 1 }}>
                {checkinKioskId && (
                  <div style={{ height: '92px', marginTop: '55px' }}>
                    <Button
                      variant="contained"
                      sx={{
                        '&:hover': { backgroundColor: 'darkorange' },
                        backgroundColor: 'orange',
                        height: '100%',
                        width: '100%',
                      }}
                      onClick={() => openDoor(checkinKioskId, true)}
                      disabled={!isAttendedMode}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap="8px"
                        style={{
                          color: '#3E2500',
                          font: 'Avenir Next',
                        }}
                      >
                        <span
                          style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '8px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <LockOpen style={{ height: 24, width: 24 }} />
                          Ouvrir la porte
                        </span>
                      </Box>
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {usesNewCameraConfig ? (
              <StoreCameraViewer
                cameraListError={cameraListQuery.error}
                cameraListPending={cameraListQuery.isPending}
                cameras={cameraListQuery.data ?? []}
                runtime={cameraConfigQuery.data?.runtime ?? null}
                storeId={Number.parseInt(storeId, 10)}
              />
            ) : (
              <div
                style={{
                  maxHeight: '80vh',
                  maxWidth: '80vh',
                  position: 'relative',
                }}
              >
                <LegacyCameraViewer
                  cameras={cameras}
                  centralCameraLabel={centralCameraLabel}
                  centralVideoId={centralVideoId}
                  mainVideoUrl={mainVideoUrl}
                  onVideoClick={handleVideoClick}
                  storeId={storeId}
                />
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              justifyContent: 'center',
            }}
          >
            <Toast />
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }} py={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: 500 }}>
                {hasResourcePermission('stores', 'read') ? (
                  <Button
                    variant="outlined"
                    onClick={() =>
                      navigate({
                        to: '/cameras/config',
                        search: { storeId: Number.parseInt(storeId, 10) },
                      })
                    }
                  >
                    Configurer les caméras de cette boutique
                  </Button>
                ) : null}
                <StoresPicker
                  onlyBoutiques
                  defaultStore={Number.parseInt(storeId, 10)}
                  setUniqueStore={handleChangeStore}
                  usedStores={boutiques?.map((s) => s.id)}
                />
              </Box>
            </Box>
            {checkoutKiosks?.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {checkoutKiosks.map((kiosk) => (
                  <KioskPanel key={kiosk.id} kiosk={kiosk} storeId={Number(storeId)} />
                ))}
              </div>
            ) : null}

            {pendingOpeningEvents && pendingOpeningEvents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div
                  style={{
                    fontFamily: 'BlockBerthold_regular',
                    fontSize: '32px',
                    fontWeight: 400,
                    lineHeight: '40px',
                    textAlign: 'left',
                  }}
                >
                  OUVERTURE
                </div>
                {pendingOpeningEvents.map((event) => {
                  const kiosk = checkinKiosks?.find((k) => k.id === event.kioskId)
                  const Card = getOpeningEventCard(event)
                  if (!Card) return null
                  const isVerified = event.type === RECEPTION_OPENING_EVENT_TYPES.METHOD_VERIFIED
                  return (
                    <Card
                      key={event.id}
                      event={event}
                      kiosk={
                        kiosk
                          ? {
                              id: kiosk.id,
                              label: kiosk.label,
                              cpiId: kiosk.cpiId,
                              storeId: Number.parseInt(storeId, 10),
                            }
                          : undefined
                      }
                      isPending={isVerified ? isConfirmOpeningPending : isConfirmMethodPending}
                      onDecision={(kioskCpiId, granted, openingEventId) =>
                        isVerified
                          ? mutateConfirmOpening({
                              kioskCpiId,
                              data: { method: event.method, granted, openingEventId },
                            })
                          : mutateConfirmMethod({
                              kioskCpiId,
                              data: { method: event.method, granted, openingEventId },
                            })
                      }
                    />
                  )
                })}
              </div>
            )}
            <div style={{ height: '80%', overflowY: 'auto' }}>
              <AnomalyCards anomalies={anomalies} onDelete={handleDeleteAnomaly} />
            </div>
            <div style={{ flexGrow: 1 }} />
          </div>
        </div>
      </div>
    </Page>
  )
}

function LegacyCameraViewer({
  cameras,
  centralCameraLabel,
  centralVideoId,
  mainVideoUrl,
  onVideoClick,
  storeId,
}: {
  cameras: AiVideoCamera[] | undefined
  centralCameraLabel: string | undefined
  centralVideoId: string | null
  mainVideoUrl: string | undefined
  onVideoClick: (videoId: string) => void
  storeId: string
}) {
  const mainLabel = centralCameraLabel ?? (centralVideoId ? `caméra ${centralVideoId}` : 'caméra')
  return (
    <>
      <div style={{ aspectRatio: 1.52, maxHeight: '80vh', maxWidth: '80vh' }}>
        <img
          alt={mainLabel}
          src={mainVideoUrl}
          style={{
            aspectRatio: 1.5,
            border: '8px solid #FF9900',
            borderRadius: '16px',
            height: '100%',
            width: '100%',
          }}
        />
        {centralVideoId ? (
          <div
            title={`ID: ${centralVideoId}`}
            style={{ fontSize: 14, color: '#374151', marginTop: 4, textAlign: 'center' }}
          >
            {mainLabel}
          </div>
        ) : null}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '21px',
          overflowX: 'auto',
        }}
      >
        {cameras?.map((camera) => (
          <CameraWindow
            key={camera.id}
            videoId={camera.id}
            label={camera.label}
            storeId={storeId}
            onClick={() => onVideoClick(camera.id)}
            isSelected={centralVideoId === camera.id}
          />
        ))}
      </div>
    </>
  )
}

interface KioskPanelProps {
  kiosk: KioskType
  storeId: number
}

const KioskPanel = ({ kiosk, storeId }: KioskPanelProps) => {
  const { cart } = kiosk

  return (
    <div
      style={{
        border: '1px solid #D9D9D9',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '16px',
      }}
    >
      <div
        style={{
          fontFamily: 'Avenir Next',
          fontSize: '18px',
          fontWeight: 600,
        }}
      >
        {kiosk.label}
      </div>

      <InterventionButtons checkType="" kioskId={kiosk.id} storeId={storeId} />

      {cart && cart.articles && cart.articles.length > 0 && (
        <div>
          <div
            style={{
              color: '#2E7D32',
              fontFamily: 'Avenir Next',
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '8px',
            }}
          >
            Panier en cours ({cart.articles.length} articles)
          </div>
          <ul
            style={{
              fontFamily: 'Avenir Next',
              fontSize: '14px',
              lineHeight: '1.4',
              margin: 0,
              maxHeight: '150px',
              overflowY: 'auto',
              paddingLeft: '16px',
            }}
          >
            {cart.articles.map((article: UniqueArticle) => (
              <li
                key={article.id}
                style={{
                  color: '#333',
                  marginBottom: '4px',
                }}
              >
                {article.product?.label ?? 'Article'}&nbsp;&nbsp;(
                {article.product.price}€)
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
